// src/components/admin/VariantManager/VariantManager.jsx - WITH EDIT FUNCTIONALITY
import React, { useState } from 'react';
import './VariantManager.css';

const VariantManager = ({
  formData,
  variantForm,
  colors,
  materials,
  materialsByCategory,
  onVariantTypeChange,
  onVariantFormChange,
  onAddVariant,
  onRemoveVariant,
  onShowColorModal,
  onShowMaterialModal
}) => {
  const [editingIndex, setEditingIndex] = useState(null);

  if (!formData.hasVariants) return null;

  // Helper to toggle item in array
  const toggleArrayItem = (array, item) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };

  // Load variant into form for editing
  const handleEditVariant = (index) => {
    const variant = formData.variants[index];
    
    // Populate the form with variant data
    if (variant.dimensions) {
      onVariantFormChange('dimensions', variant.dimensions);
    }
    
    if (variant.colors) {
      onVariantFormChange('colors', Array.isArray(variant.colors) ? variant.colors : [variant.colors]);
    }
    
    if (variant.materials) {
      onVariantFormChange('materials', Array.isArray(variant.materials) ? variant.materials : [variant.materials]);
    }
    
    if (variant.compartiment) {
      onVariantFormChange('compartiment', variant.compartiment);
    }
    
    if (variant.price) {
      onVariantFormChange('price', variant.price.toString());
    }
    
    setEditingIndex(index);
  };

  // Update existing variant
  const handleUpdateVariant = () => {
    if (editingIndex === null) return;
    
    // Call the parent's add function which will handle the update
    onAddVariant(editingIndex);
    setEditingIndex(null);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingIndex(null);
    // Reset form
    onVariantFormChange('dimensions', { width: '', height: '', length: '' });
    onVariantFormChange('colors', []);
    onVariantFormChange('materials', []);
    onVariantFormChange('compartiment', '');
    onVariantFormChange('price', '');
  };

  return (
    <div className="variant-manager">
      {/* Compact Type Selection */}
      <div className="variant-types-section">
        <div className="variant-type-checkboxes">
          <label>
            <input
              type="checkbox"
              checked={formData.variantTypes.hasDimensions}
              onChange={(e) => onVariantTypeChange('hasDimensions', e.target.checked)}
            />
            <span>📏 Dimensions</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={formData.variantTypes.hasColors}
              onChange={(e) => onVariantTypeChange('hasColors', e.target.checked)}
            />
            <span>🎨 Couleurs</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={formData.variantTypes.hasMaterials}
              onChange={(e) => onVariantTypeChange('hasMaterials', e.target.checked)}
            />
            <span>🔨 Matériaux</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={formData.variantTypes.hasCompartiments}
              onChange={(e) => onVariantTypeChange('hasCompartiments', e.target.checked)}
            />
            <span>📦 Compartiments</span>
          </label>
        </div>
      </div>

      {/* Compact Variant Form */}
      <div className={`variant-form-section ${editingIndex !== null ? 'editing' : ''}`}>
        <label>
          {editingIndex !== null ? '✏️ Modifier la variante' : '➕ Ajouter une variante'}
        </label>
        <div className="variant-form-grid">
          
          {/* Inline Dimensions */}
          {formData.variantTypes.hasDimensions && (
            <div className="variant-dimensions">
              <input
                type="number"
                placeholder="L (cm)"
                value={variantForm.dimensions.width}
                onChange={(e) => onVariantFormChange('dimensions', { 
                  ...variantForm.dimensions, 
                  width: e.target.value 
                })}
                min="0"
                step="0.1"
              />
              <input
                type="number"
                placeholder="H (cm)"
                value={variantForm.dimensions.height}
                onChange={(e) => onVariantFormChange('dimensions', { 
                  ...variantForm.dimensions, 
                  height: e.target.value 
                })}
                min="0"
                step="0.1"
              />
              <input
                type="number"
                placeholder="P (cm)"
                value={variantForm.dimensions.length}
                onChange={(e) => onVariantFormChange('dimensions', { 
                  ...variantForm.dimensions, 
                  length: e.target.value 
                })}
                min="0"
                step="0.1"
              />
            </div>
          )}

          {/* Compact Color Multi-Select */}
          {formData.variantTypes.hasColors && (
            <div className="variant-multi-select-section">
              <div className="variant-select-header">
                <label className="multi-select-label">🎨 Couleurs</label>
                <button
                  type="button"
                  className="btn-add-attribute-small"
                  onClick={onShowColorModal}
                  title="Ajouter couleur"
                >
                  +
                </button>
              </div>
              <div className="multi-select-grid">
                {colors.map(color => (
                  <label key={color.id} className="multi-select-item">
                    <input
                      type="checkbox"
                      checked={(variantForm.colors || []).includes(color.value)}
                      onChange={() => {
                        const newColors = toggleArrayItem(variantForm.colors || [], color.value);
                        onVariantFormChange('colors', newColors);
                      }}
                    />
                    <div 
                      className="color-swatch-small" 
                      style={{ backgroundColor: color.hex }}
                      title={color.label}
                    />
                    <span className="multi-select-text">{color.label}</span>
                  </label>
                ))}
              </div>
              {(variantForm.colors || []).length > 0 && (
                <div className="selected-items-preview">
                  <strong>✓</strong> {(variantForm.colors || []).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Compact Material Multi-Select */}
          {formData.variantTypes.hasMaterials && (
            <div className="variant-multi-select-section">
              <div className="variant-select-header">
                <label className="multi-select-label">🔨 Matériaux</label>
                <button
                  type="button"
                  className="btn-add-attribute-small"
                  onClick={onShowMaterialModal}
                  title="Ajouter matériau"
                >
                  +
                </button>
              </div>
              <div className="multi-select-grid">
                {Object.entries(materialsByCategory).map(([category, mats]) => (
                  <React.Fragment key={category}>
                    <div className="material-category-label">{category}</div>
                    {mats.map(material => (
                      <label key={material.id} className="multi-select-item">
                        <input
                          type="checkbox"
                          checked={(variantForm.materials || []).includes(material.value)}
                          onChange={() => {
                            const newMaterials = toggleArrayItem(
                              variantForm.materials || [], 
                              material.value
                            );
                            onVariantFormChange('materials', newMaterials);
                          }}
                        />
                        <span className="multi-select-text">{material.label}</span>
                      </label>
                    ))}
                  </React.Fragment>
                ))}
              </div>
              {(variantForm.materials || []).length > 0 && (
                <div className="selected-items-preview">
                  <strong>✓</strong> {(variantForm.materials || []).join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Compartiment Input */}
          {formData.variantTypes.hasCompartiments && (
            <div className="variant-input-full">
              <input
                type="text"
                placeholder="📦 Compartiment (ex: 3 tiroirs, 2 étagères)"
                value={variantForm.compartiment || ''}
                onChange={(e) => onVariantFormChange('compartiment', e.target.value)}
              />
            </div>
          )}

          {/* Inline Price + Add/Update Button */}
          <div className="variant-form-actions">
            <input
              type="number"
              placeholder="💰 Prix (DA) *"
              value={variantForm.price}
              onChange={(e) => onVariantFormChange('price', e.target.value)}
              min="0"
              step="0.01"
              className="variant-price-input"
            />
            
            {editingIndex !== null ? (
              <>
                <button
                  type="button"
                  onClick={handleUpdateVariant}
                  className="btn-update-variant"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Mettre à jour
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="btn-cancel-edit"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Annuler
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => onAddVariant()}
                className="btn-add-variant"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Ajouter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Compact Variants List */}
      {formData.variants.length > 0 && (
        <div className="form-group full-width">
          <label>📋 Variantes ajoutées ({formData.variants.length})</label>
          <div className="variants-list">
            {formData.variants.map((variant, index) => (
              <div 
                key={index} 
                className={`variant-item ${editingIndex === index ? 'editing' : ''}`}
              >
                <div className="variant-info">
                  <span className="variant-name">{variant.name || `Variante ${index + 1}`}</span>
                  <span className="variant-price">{variant.price.toLocaleString()} DA</span>
                  
                  {variant.dimensions && (
                    <span className="variant-detail">
                      📏 {variant.dimensions.width}×{variant.dimensions.height}×{variant.dimensions.length}cm
                    </span>
                  )}
                  
                  {variant.colors && variant.colors.length > 0 && (
                    <span className="variant-detail">
                      🎨 {variant.colors.join('+')}
                    </span>
                  )}
                  
                  {variant.materials && variant.materials.length > 0 && (
                    <span className="variant-detail">
                      🔨 {variant.materials.join('+')}
                    </span>
                  )}
                  
                  {variant.compartiment && (
                    <span className="variant-detail">
                      📦 {variant.compartiment}
                    </span>
                  )}
                </div>
                
                <div className="variant-actions">
                  <button
                    type="button"
                    className="btn-edit-small"
                    onClick={() => handleEditVariant(index)}
                    title="Modifier"
                    disabled={editingIndex !== null && editingIndex !== index}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  
                  <button
                    type="button"
                    className="btn-delete-small"
                    onClick={() => onRemoveVariant(index)}
                    title="Supprimer"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantManager;